import React, { useState, useEffect, type FunctionComponent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { DateWithUTCTooltip } from '../common'
import { authenticatedFetchData, API_HOST, BROWSER_TIMEZONE } from '../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { ConsumerSessionsTable } from '../common/ConsumerSessionsTable'
import { Notes } from '../common/notes/Notes'
import { Card } from '@tomra/design-system-react/card'

export const PayoutPage: FunctionComponent = () => {
  const { payoutId: sourceId } = useParams()
  const [payoutInfo, setPayoutInfo] = useState<PayoutInfoType>()
  const [findPayoutInfoStatus, setFindPayoutInfoStatus] = useState('loading')

  useEffect(() => {
    const f = authenticatedFetchData(
      `${API_HOST}/mytomra/v1.0/hotlinetool/payout?payoutid=${encodeURIComponent(sourceId || '')}`
    )

    f.run().then(
      (payoutInfo: PayoutInfoType) => {
        setFindPayoutInfoStatus('')
        setPayoutInfo(payoutInfo)
      },
      (error: Error) => {
        if (error.name !== 'AbortError') {
          setFindPayoutInfoStatus('failed')
          logError(new Error('Failed to fetch payout info.'), error)
        }
      }
    )

    return () => f.abort()
  }, [sourceId])

  if (findPayoutInfoStatus === 'failed') {
    return (
      <div>
        <h1 className="sr-only">Payout information</h1>
        <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute">
          Whoopsie! Failed to payout info. Please try again.
        </div>
      </div>
    )
  }

  if (payoutInfo) {
    const { consumerSessions, payout } = payoutInfo
    const {
      amount,
      clearingHouse,
      created,
      currency,
      id,
      paymentProvider,
      userId,
      payoutResults,
      latestStatus,
      errorCategory
    } = payout

    return (
      <div className="p-6">
        <h1 className="text-xl mb-4">Payout information</h1>

        <div className="flex">
          <div className="mr-2 w-1/4">
            <Card.Root className="mb-6">
              <Card.Content>
                <dl className="mb-4">
                  <dt id="payoutId" className="mb-2 text-gray-600">
                    Payout ID
                  </dt>
                  <dd aria-labelledby="payoutId">{id}</dd>
                </dl>

                <dl className="mb-4">
                  <dt id="date" className="mb-2 text-gray-600">
                    Date ({BROWSER_TIMEZONE})
                  </dt>
                  <dd aria-labelledby="date">
                    <DateWithUTCTooltip date={created} />
                  </dd>
                </dl>

                <dl className="mb-4">
                  <dt id="amount" className="mb-2 text-gray-600">
                    Amount
                  </dt>
                  <dd aria-labelledby="amount">
                    {amount && amount.toFixed(2)} {currency}
                  </dd>
                </dl>

                <dl className="mb-4">
                  <dt id="status" className="mb-2 text-gray-600">
                    Latest status
                  </dt>
                  <dd aria-labelledby="status">
                    {latestStatus}
                    {errorCategory && ` (${errorCategory})`}
                  </dd>
                </dl>

                <dl className="mb-4">
                  <dt id="clearingHouse" className="mb-2 text-gray-600">
                    Clearing house
                  </dt>
                  <dd aria-labelledby="clearingHouse">{clearingHouse}</dd>
                </dl>

                <dl className="mb-4">
                  <dt id="payoutProvider" className="mb-2 text-gray-600">
                    Payout provider
                  </dt>
                  <dd aria-labelledby="payoutProvider">{paymentProvider}</dd>
                </dl>

                <dl className="mb-4">
                  <dt id="userId" className="mb-2 text-gray-600">
                    User ID
                  </dt>
                  <dd aria-labelledby="userId">
                    <Link to={`/user/${userId}`} className="text-cyan-700 underline dark:text-cyan-400">
                      {userId}
                    </Link>
                  </dd>
                </dl>
              </Card.Content>
            </Card.Root>

            <Notes sourceId={sourceId!} />
          </div>

          <div className="ml-2 w-3/4">
            <Card.Root>
              <Card.Header>
                <h2 className="text-lg font-medium">Payout history</h2>
              </Card.Header>

              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Version</th>
                    <th>Finalized date ({BROWSER_TIMEZONE})</th>
                    <th>External ID</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutResults &&
                    payoutResults.map(({ externalId, finalized, status, version, payoutId }) => (
                      <tr key={payoutId + externalId}>
                        <td>{status}</td>
                        <td>{version}</td>
                        <td>{finalized && <DateWithUTCTooltip date={finalized} />}</td>
                        <td>{externalId}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Card.Root>

            <Card.Root className="mt-6">
              <Card.Header>
                <h2 className="text-lg font-medium">Consumer sessions</h2>
              </Card.Header>

              <ConsumerSessionsTable consumerSessions={consumerSessions} />
            </Card.Root>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="centerAbsolute">
      <div className="loadingSpinner" />
    </div>
  )
}
